/**
 * Global command palette (⌘K / Ctrl+K) for fast page navigation.
 *
 * Catalogs every user-facing route in one fuzzy-searchable surface so
 * power users can jump anywhere in two keystrokes — including pages
 * we'd never crowd into the sidebar (deep settings, admin panels,
 * individual artist tools, etc.).
 *
 * Rendering:
 *   - Mounted once in Layout (or anywhere reachable from the auth tree)
 *   - Listens globally for ⌘K / Ctrl+K to open
 *   - Esc / outside-click to close
 *   - Arrow keys + Enter to navigate (handled by cmdk via shadcn)
 *
 * Visibility of entries respects role flags from useAuth so non-artists
 * don't see artist-only destinations.
 */
import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Home, Search, Library, Upload, LayoutDashboard, User,
  Music, ListMusic, Radio, MessageCircle, Users, DollarSign,
  Settings, Trophy, Shield, Disc, Bot, Activity, Crown, Ticket,
  Store, Mic, Megaphone, MapPin, FileText, BarChart3, Coins,
  Sliders, Headphones, ShoppingBag, ShoppingCart, Sparkles, Repeat,
  Film, PartyPopper, Briefcase, Heart, CreditCard, Calendar,
  CircleHelp, ShieldCheck, Mail, Newspaper, Globe,
} from 'lucide-react';
import {
  CommandDialog, CommandInput, CommandList, CommandEmpty,
  CommandGroup, CommandItem, CommandSeparator,
} from './ui/command';
import { useAuth } from '../context/AuthContext';

/**
 * Custom cmdk filter — biased toward label prefix > label substring >
 * keyword substring. Guarantees that typing "royalt" returns
 * /royalties first, not whatever cmdk's default fuzzy match prefers.
 *
 * `value` is the item's label (lowercased by cmdk).
 * `search` is the user's query (also lowercased by cmdk).
 * `keywords` is the array we pass on CommandItem (lowercased by cmdk).
 */
function paletteFilter(value, search, keywords) {
  if (!search) return 1;
  const q = search.trim();
  if (!q) return 1;
  const v = (value || '').toLowerCase();
  const s = q.toLowerCase();
  if (v.startsWith(s)) return 1;            // prefix wins
  if (v.includes(s)) return 0.8;            // substring of label
  // Word-boundary inside label
  if (new RegExp('\\b' + s.replace(/[.*+?^${}()|[\\]\\\\]/g, '\\\\$&')).test(v)) {
    return 0.7;
  }
  // Keyword substring
  const kws = (keywords || []).map((k) => (k || '').toLowerCase());
  if (kws.some((k) => k.startsWith(s))) return 0.5;
  if (kws.some((k) => k.includes(s))) return 0.3;
  return 0;
}

/** All addressable routes, grouped. Each entry: { label, hint, to, icon, gate }
 *  `gate` is one of: 'public', 'auth', 'creator', 'admin', 'dj', 'promoter',
 *  'advertiser', 'reviewer'. */
const ROUTES = [
  // ===== Discover
  { group: 'Discover',  label: 'Home',                hint: 'Your feed',                       to: '/home',                icon: Home,          gate: 'auth' },
  { group: 'Discover',  label: 'Browse',              hint: 'All tracks · albums · videos',    to: '/browse',              icon: Search,        gate: 'public' },
  { group: 'Discover',  label: 'Discover Artists',    hint: 'Find new humans',                 to: '/discover/artists',    icon: Users,         gate: 'public' },
  { group: 'Discover',  label: 'Discover Producers',  hint: 'Beat-makers + mixers',            to: '/discover/producers',  icon: Sliders,       gate: 'public' },
  { group: 'Discover',  label: 'AI Discover',         hint: 'AI-generated catalog',            to: '/ai-discover',         icon: Bot,           gate: 'public' },
  { group: 'Discover',  label: 'AI Detector',         hint: 'Scan a track for AI content',     to: '/ai-detector',         icon: ShieldCheck,   gate: 'public' },
  { group: 'Discover',  label: 'Leaderboard',         hint: 'Top earners + plays',             to: '/leaderboard',         icon: Trophy,        gate: 'public' },
  { group: 'Discover',  label: 'Top Tracks',          hint: 'All-time chart',                  to: '/top-tracks',          icon: Trophy,        gate: 'public' },
  { group: 'Discover',  label: 'Weekly Top Tracks',   hint: 'This week\u2019s chart',          to: '/top-tracks/weekly',   icon: Trophy,        gate: 'public' },
  { group: 'Discover',  label: 'Song Wars Leaderboard', hint: 'Tournament champions',          to: '/song-wars/leaderboard', icon: Trophy,      gate: 'public' },
  { group: 'Discover',  label: 'Communities',         hint: 'Group hubs',                      to: '/communities',         icon: Users,         gate: 'public' },
  { group: 'Discover',  label: 'Collaborations',      hint: 'Find collaborators',              to: '/collaborations',      icon: Users,         gate: 'auth' },
  { group: 'Discover',  label: 'Mood Playlists',      hint: 'Curated by vibe',                 to: '/mood-playlists',      icon: Music,         gate: 'public' },
  { group: 'Discover',  label: 'Music Review',        hint: 'Get feedback on your track',      to: '/music-review',        icon: Headphones,    gate: 'public' },

  // ===== Listen
  { group: 'Listen',    label: 'OneVoice Radio',      hint: 'Dynamic live station',            to: '/radio',               icon: Radio,         gate: 'public' },
  { group: 'Listen',    label: 'AI Radio',            hint: 'Generated-music station',         to: '/ai-radio',            icon: Bot,           gate: 'public' },
  { group: 'Listen',    label: 'Free Radio',          hint: 'Free-tier radio',                 to: '/freeradio',           icon: Radio,         gate: 'public' },
  { group: 'Listen',    label: 'Music Radio',         hint: 'Music-only feed',                 to: '/musicradio',          icon: Radio,         gate: 'public' },
  { group: 'Listen',    label: 'Beats Radio',         hint: 'Producer beats',                  to: '/beatsradio',          icon: Disc,          gate: 'public' },
  { group: 'Listen',    label: 'Free Videos',         hint: 'Watch without paying',            to: '/free-videos',         icon: Film,          gate: 'public' },
  { group: 'Listen',    label: 'Free Player',         hint: 'Background music',                to: '/free-player',         icon: Music,         gate: 'public' },
  { group: 'Listen',    label: 'Listening Parties',   hint: 'Tune in together',                to: '/listening-parties',   icon: PartyPopper,   gate: 'public' },
  { group: 'Listen',    label: 'Live Sessions',       hint: 'Live artist rooms',               to: '/live-sessions',       icon: Mic,           gate: 'public' },
  { group: 'Listen',    label: 'Listener Wall',       hint: 'Public shout-outs',               to: '/listener-wall',       icon: MessageCircle, gate: 'public' },
  { group: 'Listen',    label: 'Videos',              hint: 'Music videos',                    to: '/videos',              icon: Film,          gate: 'public' },

  // ===== Library
  { group: 'Library',   label: 'My Library',          hint: 'Your saved music',                to: '/library',             icon: Library,       gate: 'auth' },
  { group: 'Library',   label: 'Playlists',           hint: 'Your playlists',                  to: '/playlists',           icon: ListMusic,     gate: 'auth' },
  { group: 'Library',   label: 'Wishlist',            hint: 'Merch + content saved',           to: '/merch/wishlist',      icon: Heart,         gate: 'auth' },
  { group: 'Library',   label: 'Cart',                hint: 'Pending checkout',                to: '/cart',                icon: ShoppingCart,  gate: 'auth' },

  // ===== Creator / Artist
  { group: 'Creator',   label: 'Dashboard',           hint: 'Artist dashboard',                to: '/dashboard',           icon: LayoutDashboard, gate: 'creator' },
  { group: 'Creator',   label: 'Upload',              hint: 'New track · album · video',       to: '/upload',              icon: Upload,        gate: 'creator' },
  { group: 'Creator',   label: 'Beat Upload',         hint: 'Upload a beat for sale',          to: '/upload?tab=beat',     icon: Music,         gate: 'creator' },
  { group: 'Creator',   label: 'Earnings',            hint: 'Your payouts',                    to: '/earnings',            icon: DollarSign,    gate: 'creator' },
  { group: 'Creator',   label: 'Royalties & Publishing', hint: 'PRO · MLC · SoundExchange · ISWC', to: '/royalties',     icon: Shield,        gate: 'creator' },
  { group: 'Creator',   label: 'Sync Catalog',        hint: 'Pitch for TV/film',               to: '/sync-catalog',        icon: Film,          gate: 'auth' },
  { group: 'Creator',   label: 'Fan Drops (Feed)',    hint: 'Exclusive content for subs',      to: '/fan-feed',            icon: DollarSign,    gate: 'creator' },
  { group: 'Creator',   label: 'Creator Pro',         hint: 'Subscription perks',              to: '/creator-pro',         icon: Crown,         gate: 'auth' },
  { group: 'Creator',   label: 'My Shop',             hint: 'Your merch storefront',           to: '/my-shop',             icon: ShoppingBag,   gate: 'creator' },
  { group: 'Creator',   label: 'Analytics',           hint: 'Per-track stats',                 to: '/analytics',           icon: Activity,      gate: 'creator' },
  { group: 'Creator',   label: 'Creator Analytics',   hint: 'Detailed engagement',             to: '/creator-analytics',   icon: BarChart3,     gate: 'creator' },
  { group: 'Creator',   label: 'Radio Stations',      hint: 'Your owned stations',             to: '/radio-stations',      icon: Radio,         gate: 'creator' },
  { group: 'Creator',   label: 'Mix & Master Orders', hint: 'Pro mixing requests',             to: '/mix-master/orders',   icon: Sliders,       gate: 'creator' },
  { group: 'Creator',   label: 'Producer Packs',      hint: 'Sample packs',                    to: '/producer-packs',      icon: Music,         gate: 'public' },
  { group: 'Creator',   label: 'My Producer Packs',   hint: 'Your purchased packs',            to: '/producer-packs/library', icon: Library,    gate: 'auth' },

  // ===== Money / Account
  { group: 'Money',     label: 'My Subscriptions',    hint: 'Active plans + billing',          to: '/my-subscriptions',    icon: Repeat,        gate: 'auth' },
  { group: 'Money',     label: 'Paid to Listen',      hint: 'Earn from streaming',             to: '/paid-to-listen',      icon: Coins,         gate: 'auth' },
  { group: 'Money',     label: 'About Paid to Listen', hint: 'How it works',                   to: '/paid-to-listen-info', icon: CircleHelp,    gate: 'public' },
  { group: 'Money',     label: 'Invoices',            hint: 'Generate + view invoices',        to: '/invoices',            icon: FileText,      gate: 'auth' },
  { group: 'Money',     label: 'VIP Lounge',          hint: 'VIP perks',                       to: '/vip',                 icon: Crown,         gate: 'auth' },
  { group: 'Money',     label: 'Fees & Pricing',      hint: 'Platform fee table',              to: '/fees',                icon: CreditCard,    gate: 'public' },

  // ===== Marketplace
  { group: 'Market',    label: 'Merch Shop',          hint: 'Browse all merch',                to: '/merch',               icon: ShoppingBag,   gate: 'public' },
  { group: 'Market',    label: 'Gig Marketplace',     hint: 'Bookings + opportunities',        to: '/marketplace',         icon: Briefcase,     gate: 'auth' },
  { group: 'Market',    label: 'My Marketplace Listings', hint: 'Manage your gigs',            to: '/marketplace/my-listings', icon: Briefcase, gate: 'auth' },
  { group: 'Market',    label: 'Mix & Master Search', hint: 'Find mix engineers',              to: '/mix-master/search',   icon: Sliders,       gate: 'auth' },

  // ===== Events
  { group: 'Events',    label: 'Events',              hint: 'Upcoming shows',                  to: '/events',              icon: Calendar,      gate: 'public' },
  { group: 'Events',    label: 'Event Map',           hint: 'Shows near you',                  to: '/event-map',           icon: MapPin,        gate: 'public' },
  { group: 'Events',    label: 'My Events',           hint: 'Events you created',              to: '/events/my-events',    icon: Calendar,      gate: 'auth' },
  { group: 'Events',    label: 'My Tickets',          hint: 'Bought tickets',                  to: '/events/my-tickets',   icon: Ticket,        gate: 'auth' },
  { group: 'Events',    label: 'Create Event',        hint: 'Sell tickets',                    to: '/events/create',       icon: Megaphone,     gate: 'auth' },

  // ===== Tools
  { group: 'Tools',     label: 'OneVoice Studio',     hint: 'AI music tools',                  to: '/studio',              icon: Sparkles,      gate: 'public' },
  { group: 'Tools',     label: 'Studio Sessions',     hint: 'Browse / open .ovs sessions',     to: '/studio/sessions',     icon: Sparkles,      gate: 'creator' },
  { group: 'Tools',     label: 'AI Studio',           hint: 'Cover art · A&R · lyrics',        to: '/ai-studio',           icon: Bot,           gate: 'public' },
  { group: 'Tools',     label: 'AI Cover Art',        hint: 'Generate album art',              to: '/ai-studio/cover-art', icon: Sparkles,      gate: 'auth' },
  { group: 'Tools',     label: 'AI Songwriter',       hint: 'Lyric assistant',                 to: '/ai-studio/songwriter',icon: Sparkles,      gate: 'auth' },
  { group: 'Tools',     label: 'AI A&R Scout',        hint: 'A&R-grade reports',               to: '/ai-studio/ar-scout',  icon: Bot,           gate: 'auth' },
  { group: 'Tools',     label: 'Social Media Kit',    hint: 'Auto-branded posts',              to: '/social-kit',          icon: Newspaper,     gate: 'auth' },
  { group: 'Tools',     label: 'Flyer Generator',     hint: 'Show posters in seconds',         to: '/flyer-generator',     icon: Newspaper,     gate: 'auth' },
  { group: 'Tools',     label: 'Email Blast',         hint: 'Reach all your subs',             to: '/email-blast',         icon: Mail,          gate: 'auth' },

  // ===== Communicate
  { group: 'Communicate', label: 'Messages',          hint: 'DMs with collaborators',          to: '/messages',            icon: MessageCircle, gate: 'auth' },
  { group: 'Communicate', label: 'Help & FAQ',        hint: 'Common questions',                to: '/help',                icon: CircleHelp,    gate: 'public' },
  { group: 'Communicate', label: 'FAQ',               hint: 'Knowledge base',                  to: '/faq',                 icon: CircleHelp,    gate: 'public' },

  // ===== Account
  { group: 'Account',   label: 'My Profile',          hint: 'View / edit profile',             to: '/profile',             icon: User,          gate: 'auth' },
  { group: 'Account',   label: 'Settings',            hint: 'Account preferences',             to: '/settings',            icon: Settings,      gate: 'auth' },
  { group: 'Account',   label: 'Notification Settings', hint: 'Email + push preferences',      to: '/settings/notifications', icon: MessageCircle, gate: 'auth' },
  { group: 'Account',   label: 'Change Password',     hint: 'Update your password',            to: '/change-password',     icon: Shield,        gate: 'auth' },
  { group: 'Account',   label: 'Terms of Service',    hint: 'Legal',                           to: '/terms',               icon: FileText,      gate: 'public' },
  { group: 'Account',   label: 'Privacy Policy',      hint: 'Legal',                           to: '/privacy',             icon: FileText,      gate: 'public' },

  // ===== Role-specific
  { group: 'DJ',        label: 'DJ Hub',              hint: 'DJ dashboard',                    to: '/dj',                  icon: Disc,          gate: 'dj' },
  { group: 'DJ',        label: 'DJ Revenue',          hint: 'Earnings breakdown',              to: '/revenue',             icon: DollarSign,    gate: 'dj' },
  { group: 'Promoter',  label: 'Promoter Dashboard',  hint: 'Campaigns + bookings',            to: '/promoter',            icon: Megaphone,     gate: 'promoter' },
  { group: 'Reviewer',  label: 'Reviewer Dashboard',  hint: 'Priority review queue',           to: '/reviewer',            icon: Headphones,    gate: 'reviewer' },
  { group: 'Advertiser',label: 'Advertiser Dashboard', hint: 'Ad campaigns',                   to: '/advertiser',          icon: BarChart3,     gate: 'advertiser' },

  // ===== Admin
  { group: 'Admin',     label: 'Admin Panel',         hint: 'Platform controls',               to: '/admin',               icon: Shield,        gate: 'admin' },
  { group: 'Admin',     label: 'Barcode Registry',    hint: 'ISRC issuance',                   to: '/admin/barcode-registry', icon: ShieldCheck, gate: 'admin' },
  { group: 'Admin',     label: 'Paid-to-Listen Mgmt', hint: 'Pool + payouts',                  to: '/admin/paid-to-listen',icon: Coins,         gate: 'admin' },
  { group: 'Admin',     label: 'Merch Disputes',      hint: 'Refunds + claims',                to: '/admin/merch-disputes',icon: ShoppingBag,   gate: 'admin' },
  { group: 'Admin',     label: 'Notifications',       hint: 'Push + broadcast tooling',        to: '/admin/notifications', icon: MessageCircle, gate: 'admin' },
  { group: 'Admin',     label: 'Performance',         hint: 'Backend metrics',                 to: '/admin/performance',   icon: Activity,      gate: 'admin' },
];

export function CommandPalette({ open, onOpenChange }) {
  const navigate = useNavigate();
  const { user, isArtist, isProducer, isAIArtist, token } = useAuth();
  const isAuthenticated = !!token;
  const isAdmin = user?.is_admin === true;
  const isAiArtist = isAIArtist || user?.is_ai_artist === true;
  const isCreator = isArtist || isProducer || isAiArtist;

  const visible = useMemo(() => ROUTES.filter((r) => {
    switch (r.gate) {
      case 'public':     return true;
      case 'auth':       return isAuthenticated;
      case 'creator':    return isAuthenticated && isCreator;
      case 'admin':      return isAuthenticated && isAdmin;
      case 'dj':         return isAuthenticated && user?.is_dj;
      case 'promoter':   return isAuthenticated && user?.is_promoter;
      case 'advertiser': return isAuthenticated && user?.is_advertiser;
      case 'reviewer':   return isAuthenticated && user?.is_reviewer;
      default:           return false;
    }
  }), [isAuthenticated, isCreator, isAdmin, user]);

  // Group routes by section, preserving the declared order.
  const grouped = useMemo(() => {
    const seen = [];
    const map = new Map();
    for (const r of visible) {
      if (!map.has(r.group)) {
        seen.push(r.group);
        map.set(r.group, []);
      }
      map.get(r.group).push(r);
    }
    return seen.map((g) => ({ group: g, items: map.get(g) }));
  }, [visible]);

  const go = (to) => {
    onOpenChange(false);
    // Brief delay lets the dialog unmount cleanly before route change.
    setTimeout(() => navigate(to), 30);
  };

  return (
    <CommandDialog
      open={open}
      onOpenChange={onOpenChange}
      title="Search OneVoice — jump to any page"
      filter={paletteFilter}
      data-testid="command-palette"
    >
      <CommandInput
        placeholder="Search any page  ·  e.g. royalties, radio, payout…"
        data-testid="command-palette-input"
      />
      <CommandList data-testid="command-palette-list">
        <CommandEmpty>No matching page.</CommandEmpty>
        {grouped.map(({ group, items }, idx) => (
          <div key={group}>
            <CommandGroup heading={group}>
              {items.map((item) => {
                const Icon = item.icon || Globe;
                return (
                  <CommandItem
                    key={item.to + item.label}
                    value={item.label}
                    keywords={[
                      item.hint || '',
                      item.to.replace(/[/-]/g, ' '),
                      item.group,
                    ]}
                    onSelect={() => go(item.to)}
                    data-testid={`command-palette-item-${item.to}`}
                    className="cursor-pointer"
                  >
                    <Icon className="mr-2 h-4 w-4 shrink-0 text-[#888]" />
                    <span className="font-mono text-sm">{item.label}</span>
                    {item.hint && (
                      <span className="ml-auto text-[11px] text-[#666] hidden sm:inline">
                        {item.hint}
                      </span>
                    )}
                  </CommandItem>
                );
              })}
            </CommandGroup>
            {idx < grouped.length - 1 && <CommandSeparator />}
          </div>
        ))}
      </CommandList>
    </CommandDialog>
  );
}

/**
 * Hook that opens the palette on ⌘K / Ctrl+K and exposes the controlled state.
 * Returns { open, setOpen, toggle }.
 */
export function useCommandPalette() {
  const [open, setOpen] = useState(false);
  useEffect(() => {
    const onKey = (e) => {
      // ⌘K (Mac) / Ctrl+K (PC) / Ctrl+/ as a friendly alt.
      const k = e.key?.toLowerCase();
      if ((e.metaKey || e.ctrlKey) && (k === 'k' || k === '/')) {
        e.preventDefault();
        setOpen((v) => !v);
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, []);
  return { open, setOpen, toggle: () => setOpen((v) => !v) };
}

export default CommandPalette;
